import { lazy, Suspense, Fragment } from "react";
import { HelmetProvider } from "react-helmet-async";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, useParams, useSearchParams } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { ViewAsProvider } from "@/contexts/ViewAsContext";
import { ProtectedRoute, AdminRoute, AdminOnlyRoute } from "@/components/ProtectedRoute";
import { PracticeAccessGuard } from "@/components/PracticeAccessGuard";
import { CookieConsent } from "@/components/CookieConsent";
import { ViewAsToolbar } from "@/components/ViewAsToolbar";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { TaskSessionWatcher } from "@/components/TaskSessionWatcher";
import { ErrorBoundary } from "@/components/ErrorBoundary";

// Lazy-loaded pages
const LandingPage = lazy(() => import("./pages/LandingPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const CoursesPage = lazy(() => import("./pages/CoursesPage"));
const CourseDetailPage = lazy(() => import("./pages/CourseDetailPage"));
const LessonViewPage = lazy(() => import("./pages/LessonViewPage"));

const PracticePage = lazy(() => import("./pages/PracticePage"));
const DailyChallengePage = lazy(() => import("./pages/DailyChallengePage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const LeaderboardPage = lazy(() => import("./pages/LeaderboardPage"));
const ArticlesPage = lazy(() => import("./pages/ArticlesPage"));
const ArticleDetailPage = lazy(() => import("./pages/ArticleDetailPage"));
const DailyWordPage = lazy(() => import("./pages/DailyWordPage"));
const TaskSelectPage = lazy(() => import("./pages/practice/TaskSelectPage"));
const FillGapsPage = lazy(() => import("./pages/practice/FillGapsPage"));
const TrueFalsePage = lazy(() => import("./pages/practice/TrueFalsePage"));
const FlashcardsPage = lazy(() => import("./pages/practice/FlashcardsPage"));
const ChooseImagePage = lazy(() => import("./pages/practice/ChooseImagePage"));
const MultipleChoicePage = lazy(() => import("./pages/practice/MultipleChoicePage"));
const DragFillPage = lazy(() => import("./pages/practice/DragFillPage"));
const MarkTheWordsPage = lazy(() => import("./pages/practice/MarkTheWordsPage"));
const DictationPage = lazy(() => import("./pages/practice/DictationPage"));
const SortParagraphsPage = lazy(() => import("./pages/practice/SortParagraphsPage"));
const DragDropImagePage = lazy(() => import("./pages/practice/DragDropImagePage"));
const ImageHotspotsPage = lazy(() => import("./pages/practice/ImageHotspotsPage"));
const CrosswordPage = lazy(() => import("./pages/practice/CrosswordPage"));
const FindTheWordsPage = lazy(() => import("./pages/practice/FindTheWordsPage"));
const GuessTheAnswerPage = lazy(() => import("./pages/practice/GuessTheAnswerPage"));
const ImageSequencingPage = lazy(() => import("./pages/practice/ImageSequencingPage"));
const ImageSliderPage = lazy(() => import("./pages/practice/ImageSliderPage"));
const SingleChoiceSetPage = lazy(() => import("./pages/practice/SingleChoiceSetPage"));
const PersonalityQuizPage = lazy(() => import("./pages/practice/PersonalityQuizPage"));
const AdvancedFillBlanksPage = lazy(() => import("./pages/practice/AdvancedFillBlanksPage"));
const MemoryGamePage = lazy(() => import("./pages/practice/MemoryGamePage"));
const EssayPage = lazy(() => import("./pages/practice/EssayPage"));
const SummaryPage = lazy(() => import("./pages/practice/SummaryPage"));
const ImagePairingPage = lazy(() => import("./pages/practice/ImagePairingPage"));
const FindTheHotspotPage = lazy(() => import("./pages/practice/FindTheHotspotPage"));
const ArithmeticQuizPage = lazy(() => import("./pages/practice/ArithmeticQuizPage"));
const SpeakTheWordsPage = lazy(() => import("./pages/practice/SpeakTheWordsPage"));
const AdminDashboardPage = lazy(() => import("./pages/admin/AdminDashboardPage"));
const AdminCoursesPage = lazy(() => import("./pages/admin/AdminCoursesPage"));
const AdminTasksPage = lazy(() => import("./pages/admin/AdminTasksPage"));
const AdminTaskEditorPage = lazy(() => import("./pages/admin/AdminTaskEditorPage"));
const AdminUsersPage = lazy(() => import("./pages/admin/AdminUsersPage"));
const AdminLessonsPage = lazy(() => import("./pages/admin/AdminLessonsPage"));
const AdminArticlesPage = lazy(() => import("./pages/admin/AdminArticlesPage"));
const AdminDailyWordsPage = lazy(() => import("./pages/admin/AdminDailyWordsPage"));
const AdminCourseTypesPage = lazy(() => import("./pages/admin/AdminCourseTypesPage"));
const AdminChatPage = lazy(() => import("./pages/admin/AdminChatPage"));
const AdminTeacherPage = lazy(() => import("./pages/admin/AdminTeacherPage"));
const AdminVocabularyPage = lazy(() => import("./pages/admin/AdminVocabularyPage"));
const AdminEmailTemplatesPage = lazy(() => import("./pages/admin/AdminEmailTemplatesPage"));
const AdminTelegramPage = lazy(() => import("./pages/admin/AdminTelegramPage"));
const AdminTtsCachePage = lazy(() => import("./pages/admin/AdminTtsCachePage"));
const AdminAiUsagePage = lazy(() => import("./pages/admin/AdminAiUsagePage"));
const AdminSubscriptionsPage = lazy(() => import("./pages/admin/AdminSubscriptionsPage"));
const AdminSettingsPage = lazy(() => import("./pages/admin/AdminSettingsPage"));
const AdminAiPromptsPage = lazy(() => import("./pages/admin/AdminAiPromptsPage"));
const AdminExamResultsPage = lazy(() => import("./pages/admin/AdminExamResultsPage"));
const AdminEllinomathiaPage = lazy(() => import("./pages/admin/AdminEllinomathiaPage"));
const AdminDialoguesPage = lazy(() => import("./pages/admin/AdminDialoguesPage"));
const AdminWPAudioPage = lazy(() => import("./pages/admin/AdminWPAudioPage"));
const AdminWPTablePage = lazy(() => import("./pages/admin/AdminWPTablePage"));
const AdminWPPdfPage = lazy(() => import("./pages/admin/AdminWPPdfPage"));
const AdminWPAttachmentsPage = lazy(() => import("./pages/admin/AdminWPAttachmentsPage"));
const AdminWPH5PPage = lazy(() => import("./pages/admin/AdminWPH5PPage"));
const AdminContentErrorsPage = lazy(() => import("./pages/admin/AdminContentErrorsPage"));
const AdminContentSearchPage = lazy(() => import("./pages/admin/AdminContentSearchPage"));
const DialoguePracticePage = lazy(() => import("./pages/speaking/DialoguePracticePage"));
const PictureDescriptionPage = lazy(() => import("./pages/speaking/PictureDescriptionPage"));
const TopicQuestionsPage = lazy(() => import("./pages/speaking/TopicQuestionsPage"));
const AdminPicturesPage = lazy(() => import("./pages/admin/AdminPicturesPage"));
const AdminTopicsPage = lazy(() => import("./pages/admin/AdminTopicsPage"));
const MessagesPage = lazy(() => import("./pages/MessagesPage"));
const LearnWordsPage = lazy(() => import("./pages/LearnWordsPage"));
const KnownWordsPage = lazy(() => import("./pages/KnownWordsPage"));
const CompletedLessonsPage = lazy(() => import("./pages/CompletedLessonsPage"));
const CompletedTasksPage = lazy(() => import("./pages/CompletedTasksPage"));
const ActivityDaysPage = lazy(() => import("./pages/ActivityDaysPage"));
const XpHistoryPage = lazy(() => import("./pages/XpHistoryPage"));
const VocabTrainerPage = lazy(() => import("./pages/VocabTrainerPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsOfUsePage = lazy(() => import("./pages/TermsOfUsePage"));
const UnsubscribePage = lazy(() => import("./pages/UnsubscribePage"));
const MaterialsPage = lazy(() => import("./pages/MaterialsPage"));
const CustomPage = lazy(() => import("./pages/CustomPage"));
const AdminHeaderPage = lazy(() => import("./pages/admin/AdminHeaderPage"));
const AdminCronLogsPage = lazy(() => import("./pages/admin/AdminCronLogsPage"));

const MistakesLogPage = lazy(() => import("./pages/MistakesLogPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000, // 2 minutes
      retry: 1,
    },
  },
});

/** Forces child remount when route params (taskId) change */
function TaskRemount({ children }: { children: React.ReactNode }) {
  const { taskId } = useParams();
  return <Fragment key={taskId}>{children}</Fragment>;
}

/** Forces child remount when search params change */
function SearchRemount({ children }: { children: React.ReactNode }) {
  const [searchParams] = useSearchParams();
  return <Fragment key={searchParams.toString()}>{children}</Fragment>;
}

const practiceRoute = (path: string, Component: React.ComponentType) => (
  <Route path={`/practice/${path}/:taskId`} element={<ProtectedRoute><PracticeAccessGuard><TaskRemount><Component /></TaskRemount></PracticeAccessGuard></ProtectedRoute>} />
);

const App = () => (
  <HelmetProvider>
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <ViewAsProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <TaskSessionWatcher />
          <ErrorBoundary>
          <Suspense fallback={<div className="flex min-h-screen items-center justify-center"><LoadingSpinner size="lg" /></div>}>
            <Routes>
              {/* Public */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/articles" element={<ArticlesPage />} />
              <Route path="/articles/:articleId" element={<ArticleDetailPage />} />
              <Route path="/daily-word" element={<DailyWordPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/terms" element={<TermsOfUsePage />} />
              <Route path="/unsubscribe" element={<UnsubscribePage />} />
              <Route path="/products" element={<MaterialsPage />} />
              <Route path="/materials" element={<MaterialsPage />} />
              <Route path="/resources" element={<MaterialsPage />} />
              <Route path="/p/:slug" element={<CustomPage />} />

              {/* Protected */}
              <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
              <Route path="/leaderboard" element={<ProtectedRoute><LeaderboardPage /></ProtectedRoute>} />
              <Route path="/courses" element={<ProtectedRoute><CoursesPage /></ProtectedRoute>} />
              <Route path="/courses/:courseId" element={<ProtectedRoute><CourseDetailPage /></ProtectedRoute>} />
              <Route path="/courses/:courseId/lessons/:lessonId" element={<ProtectedRoute><LessonViewPage /></ProtectedRoute>} />
              <Route path="/courses/:courseId/lessons/:lessonId/chapters/:chapterId" element={<ProtectedRoute><LessonViewPage /></ProtectedRoute>} />
              
              <Route path="/practice" element={<ProtectedRoute><PracticePage /></ProtectedRoute>} />
              <Route path="/practice/daily-challenge" element={<ProtectedRoute><DailyChallengePage /></ProtectedRoute>} />
              <Route path="/learn-words" element={<ProtectedRoute><SearchRemount><LearnWordsPage /></SearchRemount></ProtectedRoute>} />
              <Route path="/known-words" element={<ProtectedRoute><KnownWordsPage /></ProtectedRoute>} />
              <Route path="/completed-lessons" element={<ProtectedRoute><CompletedLessonsPage /></ProtectedRoute>} />
              <Route path="/completed-tasks" element={<ProtectedRoute><CompletedTasksPage /></ProtectedRoute>} />
              <Route path="/activity-days" element={<ProtectedRoute><ActivityDaysPage /></ProtectedRoute>} />
              <Route path="/xp-history" element={<ProtectedRoute><XpHistoryPage /></ProtectedRoute>} />
              <Route path="/vocab-trainer" element={<ProtectedRoute><SearchRemount><VocabTrainerPage /></SearchRemount></ProtectedRoute>} />
              <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
              <Route path="/mistakes" element={<ProtectedRoute><MistakesLogPage /></ProtectedRoute>} />
              <Route path="/practice/:type" element={<ProtectedRoute><TaskSelectPage /></ProtectedRoute>} />
              <Route path="/speaking/dialogues" element={<ProtectedRoute><DialoguePracticePage /></ProtectedRoute>} />
              <Route path="/speaking/pictures" element={<ProtectedRoute><PictureDescriptionPage /></ProtectedRoute>} />
              <Route path="/speaking/topics" element={<ProtectedRoute><TopicQuestionsPage /></ProtectedRoute>} />
              {practiceRoute('fill_gaps', FillGapsPage)}
              {practiceRoute('true_false', TrueFalsePage)}
              {practiceRoute('flashcards', FlashcardsPage)}
              {practiceRoute('choose_image', ChooseImagePage)}
              {practiceRoute('multiple_choice', MultipleChoicePage)}
              {practiceRoute('drag_fill', DragFillPage)}
              {practiceRoute('mark_the_words', MarkTheWordsPage)}
              {practiceRoute('dictation', DictationPage)}
              {practiceRoute('sort_paragraphs', SortParagraphsPage)}
              {practiceRoute('drag_drop_image', DragDropImagePage)}
              {practiceRoute('image_hotspots', ImageHotspotsPage)}
              {practiceRoute('crossword', CrosswordPage)}
              {practiceRoute('find_the_words', FindTheWordsPage)}
              {practiceRoute('guess_the_answer', GuessTheAnswerPage)}
              {practiceRoute('image_sequencing', ImageSequencingPage)}
              {practiceRoute('image_slider', ImageSliderPage)}
              {practiceRoute('single_choice_set', SingleChoiceSetPage)}
              {practiceRoute('personality_quiz', PersonalityQuizPage)}
              {practiceRoute('advanced_fill_blanks', AdvancedFillBlanksPage)}
              {practiceRoute('memory_game', MemoryGamePage)}
              {practiceRoute('essay', EssayPage)}
              {practiceRoute('summary', SummaryPage)}
              {practiceRoute('image_pairing', ImagePairingPage)}
              {practiceRoute('find_the_hotspot', FindTheHotspotPage)}
              {practiceRoute('arithmetic_quiz', ArithmeticQuizPage)}
              {practiceRoute('speak_the_words', SpeakTheWordsPage)}

              {/* Admin */}
              <Route path="/admin" element={<AdminRoute><AdminDashboardPage /></AdminRoute>} />
              <Route path="/admin/settings" element={<AdminOnlyRoute><AdminSettingsPage /></AdminOnlyRoute>} />
              <Route path="/admin/header" element={<AdminOnlyRoute><AdminHeaderPage /></AdminOnlyRoute>} />
              <Route path="/admin/cron-logs" element={<AdminOnlyRoute><AdminCronLogsPage /></AdminOnlyRoute>} />
              <Route path="/admin/course-types" element={<AdminRoute><AdminCourseTypesPage /></AdminRoute>} />
              <Route path="/admin/courses" element={<AdminRoute><AdminCoursesPage /></AdminRoute>} />
              <Route path="/admin/tasks" element={<AdminRoute><AdminTasksPage /></AdminRoute>} />
              <Route path="/admin/tasks/new" element={<AdminRoute><AdminTaskEditorPage /></AdminRoute>} />
              <Route path="/admin/tasks/:taskId/edit" element={<AdminRoute><AdminTaskEditorPage /></AdminRoute>} />
              <Route path="/admin/wp-audio" element={<AdminRoute><AdminWPAudioPage /></AdminRoute>} />
              <Route path="/admin/wp-tables" element={<AdminRoute><AdminWPTablePage /></AdminRoute>} />
              <Route path="/admin/wp-pdfs" element={<AdminRoute><AdminWPPdfPage /></AdminRoute>} />
              <Route path="/admin/wp-attachments" element={<AdminRoute><AdminWPAttachmentsPage /></AdminRoute>} />
              <Route path="/admin/wp-h5p" element={<AdminRoute><AdminWPH5PPage /></AdminRoute>} />
              <Route path="/admin/content-errors" element={<AdminRoute><AdminContentErrorsPage /></AdminRoute>} />
              <Route path="/admin/content-search" element={<AdminRoute><AdminContentSearchPage /></AdminRoute>} />
              <Route path="/admin/lessons" element={<AdminRoute><AdminLessonsPage /></AdminRoute>} />
              <Route path="/admin/articles" element={<AdminRoute><AdminArticlesPage /></AdminRoute>} />
              <Route path="/admin/daily-words" element={<AdminRoute><AdminDailyWordsPage /></AdminRoute>} />
              <Route path="/admin/users" element={<AdminRoute><AdminUsersPage /></AdminRoute>} />
              <Route path="/admin/vocabulary" element={<AdminRoute><AdminVocabularyPage /></AdminRoute>} />
              <Route path="/admin/chat" element={<AdminOnlyRoute><AdminChatPage /></AdminOnlyRoute>} />
              <Route path="/admin/teacher" element={<AdminOnlyRoute><AdminTeacherPage /></AdminOnlyRoute>} />
              <Route path="/admin/email-templates" element={<AdminOnlyRoute><AdminEmailTemplatesPage /></AdminOnlyRoute>} />
              <Route path="/admin/telegram" element={<AdminOnlyRoute><AdminTelegramPage /></AdminOnlyRoute>} />
              <Route path="/admin/tts" element={<AdminOnlyRoute><AdminTtsCachePage /></AdminOnlyRoute>} />
              <Route path="/admin/ai-usage" element={<AdminOnlyRoute><AdminAiUsagePage /></AdminOnlyRoute>} />
              <Route path="/admin/ai-prompts" element={<AdminOnlyRoute><AdminAiPromptsPage /></AdminOnlyRoute>} />
              <Route path="/admin/exam-results" element={<AdminOnlyRoute><AdminExamResultsPage /></AdminOnlyRoute>} />
              <Route path="/admin/ellinomathia" element={<AdminOnlyRoute><AdminEllinomathiaPage /></AdminOnlyRoute>} />
              
              <Route path="/admin/subscriptions" element={<AdminOnlyRoute><AdminSubscriptionsPage /></AdminOnlyRoute>} />
              <Route path="/admin/dialogues" element={<AdminRoute><AdminDialoguesPage /></AdminRoute>} />
              <Route path="/admin/pictures" element={<AdminRoute><AdminPicturesPage /></AdminRoute>} />
              <Route path="/admin/topics" element={<AdminRoute><AdminTopicsPage /></AdminRoute>} />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          </ErrorBoundary>
          <CookieConsent />
          <ViewAsToolbar />
        </BrowserRouter>
      </TooltipProvider>
      </ViewAsProvider>
    </AuthProvider>
  </QueryClientProvider>
  </HelmetProvider>
);

export default App;
